<template>
  <v-dialog v-model="dialog" fullscreen hide-overlay persistent no-click-animation transition="dialog-bottom-transition">
    <div class="special-detail-page">
      <header class="special-detail-header">
        <v-btn icon class="special-back-button" aria-label="Zurück zum Group Fitness" @click="closeDialog">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <div>
          <span>Group Fitness</span>
          <strong>Member Special</strong>
        </div>
        <span class="special-header-icon" aria-hidden="true"><v-icon>mdi-star-four-points-outline</v-icon></span>
      </header>

      <main class="special-detail-main">
        <div v-if="loading" class="special-state-card">
          <v-progress-circular indeterminate color="#00963f" size="48"></v-progress-circular>
          <p>Kursinformationen werden geladen …</p>
        </div>

        <div v-else-if="errorMessage" class="special-state-card is-error">
          <span class="special-state-icon"><v-icon>mdi-alert-circle-outline</v-icon></span>
          <h1>Kurs nicht verfügbar</h1>
          <p>{{ errorMessage }}</p>
          <v-btn outlined color="#00963f" class="special-secondary-button" @click="closeDialog">Zurück zum Kursplan</v-btn>
        </div>

        <template v-else-if="course">
          <section class="special-summary">
            <span class="special-summary-eyebrow"><v-icon small>mdi-star-four-points-outline</v-icon> Exklusiv für Mitglieder</span>
            <h1>{{ course.format ? course.format.title : "Member Special" }}</h1>
            <p v-if="course.details" class="special-summary-lead">{{ course.details }}</p>
            <div class="special-summary-status">
              <span v-if="course.userRegistration" :class="{ 'is-queue': course.userRegistration.status === 'waitlisted' }">
                <v-icon small>{{ course.userRegistration.status === "waitlisted" ? "mdi-timer-sand" : "mdi-check-circle-outline" }}</v-icon>
                {{ ownRegistrationLabel }}
              </span>
              <span v-else :class="{ 'is-queue': isFull }">
                <v-icon small>{{ isFull ? "mdi-account-clock-outline" : "mdi-account-check-outline" }}</v-icon>
                {{ availabilityLabel }}
              </span>
            </div>
          </section>

          <button v-if="weeklyConflict" type="button" class="special-notice is-warning is-action" @click="openWeeklyCourse">
            <span class="special-notice-icon"><v-icon>mdi-calendar-check-outline</v-icon></span>
            <span class="special-notice-copy">
              <strong>Diese Woche bereits gebucht</strong>
              <p>{{ weeklyCourseTitle }} · {{ weeklyRegistration.status === "waitlisted" ? "Warteliste" : "Angemeldet" }}</p>
            </span>
            <span class="special-notice-action">Öffnen <v-icon small>mdi-chevron-right</v-icon></span>
          </button>

          <section v-if="isEmployeeRegistration" class="special-notice">
            <span class="special-notice-icon"><v-icon>mdi-school-outline</v-icon></span>
            <div class="special-notice-copy">
              <strong>Bereits als Mitarbeiter angemeldet</strong>
              <p>Diese Anmeldung wird weiterhin über den Mitarbeiterkurs-Bereich verwaltet und zählt nicht als Member-Special-Wochenbuchung.</p>
            </div>
          </section>

          <section class="special-facts" aria-label="Kursdetails">
            <article class="special-fact-row">
              <span class="special-fact-icon"><v-icon>mdi-calendar-clock-outline</v-icon></span>
              <div>
                <small>Termin</small>
                <strong>{{ formattedDate }}</strong>
                <span>{{ formattedTime }} Uhr · {{ course.duration }} Min.</span>
              </div>
            </article>
            <article class="special-fact-row">
              <span class="special-fact-icon"><v-icon>mdi-map-marker-outline</v-icon></span>
              <div>
                <small>Kursort</small>
                <strong>{{ course.studio ? course.studio.name : "well come FIT" }}</strong>
                <span v-if="course.studio && course.studio.adresse">{{ course.studio.adresse }}</span>
              </div>
            </article>
            <article class="special-fact-row">
              <span class="special-fact-icon"><v-icon>mdi-account-group-outline</v-icon></span>
              <div>
                <small>Mitgliederplätze</small>
                <strong>{{ customerOccupancyLabel }}</strong>
                <span>{{ occupancyNote }}</span>
              </div>
            </article>
          </section>

          <section v-if="course.format && course.format.description" class="special-content-card">
            <span class="special-section-label">Über den Kurs</span>
            <p>{{ course.format.description }}</p>
          </section>

          <section class="special-content-card special-booking-rules">
            <span class="special-section-label">Buchung & Abmeldung</span>
            <div>
              <v-icon small>mdi-calendar-week</v-icon>
              <p><strong>1 pro Woche:</strong> Eine Anmeldung je Kurs-Kalenderwoche.</p>
            </div>
            <div>
              <v-icon small>mdi-clock-alert-outline</v-icon>
              <p><strong>Bis 1 Stunde vorher:</strong> Online abmelden kannst du dich bis {{ formattedDeadline }} Uhr.</p>
            </div>
          </section>
        </template>
      </main>

      <footer v-if="course && !loading && !errorMessage" class="special-action-bar">
        <div class="special-action-copy">
          <small>{{ actionEyebrow }}</small>
          <strong>{{ actionInfo }}</strong>
        </div>
        <v-btn
          large
          :loading="submitting"
          :disabled="actionDisabled"
          :color="hasCustomerRegistration ? 'error' : '#00963f'"
          class="special-primary-button white--text"
          @click="handleBookingAction">
          <v-icon left>{{ actionIcon }}</v-icon>
          {{ actionLabel }}
        </v-btn>
      </footer>
    </div>
  </v-dialog>
</template>

<script>
import api from "@/api"
import userService from "@/services/userService"

const TIME_ZONE = "Europe/Zurich"

export default {
  name: "EmployeeCourseCustomerView",
  data() {
    return {
      dialog: true,
      loading: true,
      submitting: false,
      course: null,
      weeklyRegistration: null,
      errorMessage: "",
    }
  },
  computed: {
    hasCustomerRegistration() {
      return this.course?.userRegistration?.bookingAudience === "customer"
    },
    isEmployeeRegistration() {
      return !!this.course?.userRegistration && this.course.userRegistration.bookingAudience !== "customer"
    },
    isCustomerCapacityUnlimited() {
      return this.course?.customerCapacity === null
    },
    customerCapacity() {
      if (this.isCustomerCapacityUnlimited) return null
      return Math.max(1, Number(this.course?.customerCapacity || 4))
    },
    customerRegisteredCount() {
      return Math.max(0, Number(this.course?.customerRegisteredCount || 0))
    },
    isFull() {
      return !!this.course && this.freeSpots <= 0
    },
    freeSpots() {
      if (!this.course) return 0
      const reportedFreeSpots = Number(this.course.customerFreeSpots)
      if (Number.isFinite(reportedFreeSpots)) return Math.max(0, reportedFreeSpots)
      const courseFreeSpots = Number(this.course.capacity || 0) - Number(this.course.registeredCount || 0)
      const customerFreeSpots = this.isCustomerCapacityUnlimited ? courseFreeSpots : this.customerCapacity - this.customerRegisteredCount
      return Math.max(0, Math.min(customerFreeSpots, courseFreeSpots))
    },
    customerOccupancyLabel() {
      if (this.isCustomerCapacityUnlimited) return "Unbegrenzt freigeschaltet"
      const occupiedSpots = Math.min(this.customerRegisteredCount, this.customerCapacity)
      return occupiedSpots + " / " + this.customerCapacity + " belegt"
    },
    weeklyConflict() {
      const weeklyCourseId = this.weeklyRegistration?.course?._id
      return !!weeklyCourseId && String(weeklyCourseId) !== String(this.course?._id)
    },
    weeklyCourseTitle() {
      return this.weeklyRegistration?.course?.format?.title || "einen anderen Member-Special-Kurs"
    },
    customerCancellationDeadline() {
      if (this.course?.customerCancellationDeadline) return this.course.customerCancellationDeadline
      if (!this.course?.dateTime) return null
      return new Date(new Date(this.course.dateTime).getTime() - 60 * 60 * 1000)
    },
    cancellationOpen() {
      return !!this.customerCancellationDeadline && new Date() < new Date(this.customerCancellationDeadline)
    },
    ownRegistrationLabel() {
      if (!this.course?.userRegistration) return ""
      if (this.course.userRegistration.status === "waitlisted") return `Wartelistenplatz ${this.course.userRegistration.waitlistPosition}`
      return "Du bist angemeldet"
    },
    availabilityLabel() {
      if (this.freeSpots > 0) return `${this.freeSpots} ${this.freeSpots === 1 ? "Platz" : "Plätze"} frei`
      return "Mitgliederplätze ausgebucht"
    },
    occupancyNote() {
      if (this.isCustomerCapacityUnlimited) {
        const signupLabel =
          this.customerRegisteredCount === 1
            ? "1 Mitglied angemeldet"
            : `${this.customerRegisteredCount} Mitglieder angemeldet`
        if (this.freeSpots > 0) {
          const freeLabel = this.freeSpots === 1 ? "1 Platz im Kurs frei" : `${this.freeSpots} Plätze im Kurs frei`
          return signupLabel + " · " + freeLabel
        }
        return signupLabel + " · Kurs vollständig belegt"
      }
      if (this.freeSpots > 0) return `${this.freeSpots} ${this.freeSpots === 1 ? "Platz" : "Plätze"} frei`
      return "Keine Mitgliederplätze frei"
    },
    formattedDate() {
      if (!this.course?.dateTime) return ""
      return new Intl.DateTimeFormat("de-CH", {
        timeZone: TIME_ZONE,
        weekday: "long",
        day: "2-digit",
        month: "long",
        year: "numeric",
      }).format(new Date(this.course.dateTime))
    },
    formattedTime() {
      if (!this.course?.dateTime) return ""
      return new Intl.DateTimeFormat("de-CH", {
        timeZone: TIME_ZONE,
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
      }).format(new Date(this.course.dateTime))
    },
    formattedDeadline() {
      if (!this.customerCancellationDeadline) return ""
      return new Intl.DateTimeFormat("de-CH", {
        timeZone: TIME_ZONE,
        weekday: "short",
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
      }).format(new Date(this.customerCancellationDeadline))
    },
    actionDisabled() {
      if (this.submitting || this.isEmployeeRegistration) return true
      if (this.hasCustomerRegistration) return !this.cancellationOpen
      if (this.weeklyConflict) return !this.weeklyRegistration?.course?._id
      return this.isFull
    },
    actionLabel() {
      if (this.hasCustomerRegistration) return this.cancellationOpen ? "Abmelden" : "Frist abgelaufen"
      if (this.isEmployeeRegistration) return "Bereits angemeldet"
      if (this.weeklyConflict) return "Buchung öffnen"
      if (this.isFull) return "Ausgebucht"
      return "Jetzt anmelden"
    },
    actionEyebrow() {
      if (this.hasCustomerRegistration) return "Deine Buchung"
      if (this.weeklyConflict) return "Deine Wochenbuchung"
      if (this.isFull) return this.isCustomerCapacityUnlimited ? "Kurskapazität" : "Mitgliederkontingent"
      return "Direkte Bestätigung"
    },
    actionInfo() {
      if (this.hasCustomerRegistration) {
        return this.course.userRegistration.status === "waitlisted" ? `Wartelistenplatz ${this.course.userRegistration.waitlistPosition}` : "Platz bestätigt"
      }
      if (this.weeklyConflict) return this.weeklyCourseTitle
      if (this.isFull && this.isCustomerCapacityUnlimited) return "Kurs vollständig belegt"
      if (this.isFull) return `Alle ${this.customerCapacity} Mitgliederplätze belegt`
      return this.freeSpots === 1 ? "Noch 1 Platz frei" : `Noch ${this.freeSpots} Plätze frei`
    },
    actionIcon() {
      if (this.hasCustomerRegistration) return "mdi-close-circle-outline"
      if (this.weeklyConflict) return "mdi-arrow-right"
      if (this.isFull) return "mdi-account-clock-outline"
      return "mdi-check"
    },
  },
  watch: {
    "$route.params.id"(nextId, previousId) {
      if (nextId && nextId !== previousId) this.loadCourse()
    },
  },
  async created() {
    if (userService.hasRight("gf_fetchEmployeeCourses")) {
      this.dialog = false
      this.$router.replace("/employee-courses")
      return
    }
    document.body.style = "overflow:hidden;max-height:100vh;"
    await this.loadCourse()
  },
  beforeDestroy() {
    document.body.style = "overflow:auto;max-height:100vh;"
  },
  methods: {
    async loadCourse() {
      this.loading = true
      this.errorMessage = ""
      try {
        const data = await api.employeeCourses.customer.fetchCourse(this.$route.params.id)
        this.course = data.course
        this.weeklyRegistration = data.weeklyRegistration || null
      } catch (error) {
        console.error(error)
        this.errorMessage = this.getErrorMessage(error, "Dieser Kurs ist nicht mehr verfügbar oder für dein Center nicht freigeschaltet.")
      } finally {
        this.loading = false
      }
    },
    async handleBookingAction() {
      if (this.actionDisabled || !this.course) return
      if (this.weeklyConflict) {
        this.openWeeklyCourse()
        return
      }
      this.submitting = true
      try {
        if (this.hasCustomerRegistration) {
          const result = await api.employeeCourses.customer.unregister(this.course._id)
          this.$toast.success(result.message || "Erfolgreich abgemeldet.")
          this.$emit("updateCustomerCourses")
          this.closeDialog()
          return
        }

        const result = await api.employeeCourses.customer.register(this.course._id)
        this.$toast.success(result.message || "Buchung gespeichert.")
        await this.loadCourse()
        this.$emit("updateCustomerCourses")
      } catch (error) {
        console.error(error)
        this.$toast.error(this.getErrorMessage(error, "Die Buchung konnte nicht gespeichert werden."))
      } finally {
        this.submitting = false
      }
    },
    openWeeklyCourse() {
      const courseId = this.weeklyRegistration?.course?._id
      if (!courseId || String(courseId) === String(this.course?._id)) return
      this.$router.push({ name: "employeeCourseCustomerView", params: { id: courseId } })
    },
    getErrorMessage(error, fallback) {
      const message = error?.response?.data
      if (typeof message === "string" && message.trim()) return message
      if (message?.message) return message.message
      return fallback
    },
    closeDialog() {
      document.body.style = "overflow:auto;max-height:100vh;"
      this.dialog = false
      if (this.$route.name === "employeeCourseCustomerView") this.$router.push("/kurse")
    },
  },
}
</script>

<style scoped>
.special-detail-page {
  min-height: 100vh;
  min-height: 100dvh;
  padding-bottom: 6.5rem;
  color: #17231d;
  background: radial-gradient(circle at 82% -8%, rgba(56, 128, 108, 0.2), transparent 24rem), #e7efeb;
  font-size: 1rem;
}

.special-detail-header {
  position: sticky;
  z-index: 5;
  top: 0;
  display: grid;
  min-height: 4rem;
  grid-template-columns: 2.5rem minmax(0, 1fr) 2.5rem;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem max(1rem, env(safe-area-inset-left));
  border-bottom: 1px solid #c9d8d1;
  background: rgba(232, 241, 237, 0.96);
  backdrop-filter: blur(1rem);
}

.special-detail-header > div {
  display: grid;
}

.special-detail-header > div span {
  color: #5d756a;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  line-height: 1.3;
  text-transform: uppercase;
}

.special-detail-header > div strong {
  color: #17231d;
  font-size: 1rem;
  line-height: 1.35;
}

.special-back-button,
.special-header-icon {
  width: 2.5rem;
  height: 2.5rem;
  border: 1px solid #bfd2c9;
  border-radius: 0.875rem;
  color: #17231d;
  background: #dce9e3;
}

.special-header-icon {
  display: grid;
  place-items: center;
  border-color: #1d5145;
  color: #f0d28b;
  background: #1d5145;
}

.special-header-icon .v-icon {
  color: currentColor;
}

.special-detail-main {
  width: min(100%, 42rem);
  margin: 0 auto;
  padding: 0.75rem 1rem 1.25rem;
}

.special-state-card {
  display: grid;
  min-height: 20rem;
  place-items: center;
  align-content: center;
  gap: 1rem;
  padding: 2rem;
  border: 1px solid #c6d8cf;
  border-radius: 1.5rem;
  background: #edf4f1;
  text-align: center;
}

.special-state-card h1,
.special-state-card p {
  margin: 0;
}

.special-state-card p {
  color: #647168;
  font-size: 1rem;
  line-height: 1.55;
}

.special-state-card.is-error .special-state-icon {
  display: grid;
  width: 3.5rem;
  height: 3.5rem;
  place-items: center;
  border-radius: 1rem;
  color: #c52a2a;
  background: #fff0f0;
}

.special-state-icon .v-icon {
  color: currentColor;
}

.special-secondary-button,
.special-primary-button {
  min-height: 3rem !important;
  border-radius: 0.875rem !important;
  font-size: 1rem !important;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: none;
}

.special-summary {
  position: relative;
  overflow: hidden;
  padding: 1.125rem;
  border: 1px solid #2b6455;
  border-radius: 1.125rem;
  background: radial-gradient(circle at 100% 0%, rgba(159, 220, 194, 0.2), transparent 12rem), linear-gradient(145deg, #19473d, #266150);
  box-shadow: 0 0.5rem 1.5rem rgba(13, 44, 36, 0.16);
}

.special-summary-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  color: #bce4d2;
  font-size: 0.75rem;
  font-weight: 850;
  letter-spacing: 0.06em;
  line-height: 1.35;
  text-transform: uppercase;
}

.special-summary-eyebrow .v-icon {
  color: #f0d28b;
}

.special-summary h1 {
  margin: 0.375rem 0 0;
  color: #ffffff;
  font-size: clamp(1.625rem, 6vw, 2.125rem);
  font-weight: 900;
  line-height: 1.15;
}

.special-summary-lead {
  margin: 0.625rem 0 0;
  color: #d2e6dc;
  font-size: 1rem;
  line-height: 1.5;
  white-space: pre-line;
}

.special-summary-status {
  margin-top: 0.875rem;
}

.special-summary-status span {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.625rem;
  border-radius: 999px;
  color: #174b38;
  background: #d5ecdf;
  font-size: 0.875rem;
  font-weight: 850;
  line-height: 1.35;
}

.special-summary-status span.is-queue {
  color: #825200;
  background: rgba(255, 184, 51, 0.18);
}

.special-summary-status .v-icon {
  color: currentColor;
}

.special-notice {
  display: grid;
  width: 100%;
  grid-template-columns: 2.5rem minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.625rem;
  margin-top: 0.75rem;
  padding: 0.75rem;
  border: 1px solid #c2d9cf;
  border-radius: 0.875rem;
  outline: none;
  color: #17231d;
  background: #e6f1ec;
  font: inherit;
  text-align: left;
}

.special-notice.is-action {
  cursor: pointer;
}

.special-notice.is-action:focus-visible {
  box-shadow: 0 0 0 0.1875rem rgba(0, 150, 63, 0.2);
}

.special-notice-icon {
  display: grid;
  width: 2.5rem;
  height: 2.5rem;
  place-items: center;
  border-radius: 0.75rem;
  color: #195f49;
  background: #cfe4da;
}

.special-notice-copy {
  display: grid;
  min-width: 0;
}

.special-notice.is-warning {
  border-color: rgba(210, 135, 0, 0.24);
  background: #fffaf0;
}

.special-notice.is-warning .special-notice-icon {
  color: #9a6200;
  background: rgba(255, 184, 51, 0.16);
}

.special-notice .v-icon {
  color: currentColor;
}

.special-notice strong {
  color: #17231d;
  font-size: 1rem;
  line-height: 1.4;
}

.special-notice p {
  overflow: hidden;
  margin: 0.125rem 0 0;
  color: #5d6962;
  font-size: 0.875rem;
  line-height: 1.4;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.special-notice-action {
  display: inline-flex;
  align-items: center;
  color: #8a5900;
  font-size: 0.875rem;
  font-weight: 850;
  line-height: 1.35;
}

.special-facts {
  display: grid;
  margin-top: 0.75rem;
  overflow: hidden;
  border: 1px solid #bfd4ca;
  border-radius: 1rem;
  background: #edf4f1;
}

.special-fact-row {
  display: grid;
  min-width: 0;
  grid-template-columns: 2.5rem minmax(0, 1fr);
  align-items: center;
  gap: 0.625rem;
  padding: 0.75rem 0.875rem;
}

.special-fact-row + .special-fact-row {
  border-top: 1px solid #d3e1da;
}

.special-fact-icon {
  display: grid;
  width: 2.5rem;
  height: 2.5rem;
  place-items: center;
  border-radius: 0.75rem;
  color: #195f49;
  background: #d5e8df;
}

.special-fact-icon .v-icon {
  color: currentColor;
}

.special-fact-row > div {
  display: grid;
  min-width: 0;
}

.special-fact-row small,
.special-action-copy small,
.special-section-label {
  color: #587065;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  line-height: 1.4;
  text-transform: uppercase;
}

.special-fact-row strong {
  margin-top: 0.1875rem;
  overflow-wrap: anywhere;
  color: #17231d;
  font-size: 1rem;
  line-height: 1.4;
}

.special-fact-row > div > span {
  margin-top: 0.125rem;
  color: #6b756f;
  font-size: 0.875rem;
  line-height: 1.4;
}

.special-content-card {
  margin-top: 0.75rem;
  padding: 1rem;
  border: 1px solid #c6d8cf;
  border-radius: 1rem;
  background: #edf4f1;
}

.special-booking-rules {
  background: #dfece6;
}

.special-content-card > p {
  margin: 0.5rem 0 0;
  color: #4f5d55;
  font-size: 1rem;
  line-height: 1.55;
  white-space: pre-line;
}

.special-booking-rules > div {
  display: grid;
  grid-template-columns: 1.75rem minmax(0, 1fr);
  gap: 0.625rem;
  margin-top: 0.75rem;
}

.special-booking-rules .v-icon {
  color: #1b7357;
}

.special-booking-rules p {
  margin: 0;
  color: #536058;
  font-size: 0.875rem;
  line-height: 1.45;
}

.special-action-bar {
  position: fixed;
  z-index: 6;
  right: 0;
  bottom: 0;
  left: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem max(1rem, env(safe-area-inset-right)) calc(0.75rem + env(safe-area-inset-bottom)) max(1rem, env(safe-area-inset-left));
  border-top: 1px solid #bfd2c9;
  background: rgba(226, 238, 232, 0.97);
  box-shadow: 0 -0.5rem 1.5rem rgba(18, 44, 30, 0.08);
  backdrop-filter: blur(1rem);
}

.special-action-copy {
  display: grid;
  min-width: 0;
}

.special-action-copy strong {
  margin-top: 0.125rem;
  overflow: hidden;
  color: #17231d;
  font-size: 0.9375rem;
  line-height: 1.4;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.special-primary-button {
  min-width: 9.75rem !important;
}

@media (max-width: 37.5rem) {
  .special-detail-page {
    padding-bottom: 6.75rem;
  }
}

@media (max-width: 20rem) {
  .special-action-bar {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }

  .special-primary-button {
    width: 100%;
    min-width: 0 !important;
  }

  .special-detail-page {
    padding-bottom: 10rem;
  }
}
</style>
